import { IAnnualReport, IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';

/**
 * Returns true if any leaf account (or `reclassesAccounts` row) carried by
 * the given finStatements has a non-zero legacy `openingReClass` /
 * `closingReClass` value.
 *
 * Used in the account-mapping step of an autofilled 2nd-year report to
 * detect that the source (previous year) AR holds legacy reclass values.
 * When the current AR is post-cutover, those legacy values can no longer
 * be edited via the old reclasses tab — the user must re-enter them as
 * transactions in the new grouping/masking reclasses UI. The mapping step
 * shows a one-time warning dialog in that case.
 *
 * Defensive: tolerates missing arrays, missing templates, and partial AR
 * shapes. A `null` / `undefined` input returns `false`.
 */
export function hasLegacyReclasses(finStatements: IFinStatements[] | null | undefined): boolean {
    if (!Array.isArray(finStatements) || finStatements.length === 0) return false;

    const isNonZero = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v) && v !== 0;
    const get = (o: unknown, key: string): unknown =>
        o != null && typeof o === 'object' ? (o as Record<string, unknown>)[key] : undefined;

    const checkAccount = (acc: unknown): boolean => {
        if (acc == null || typeof acc !== 'object') return false;
        return isNonZero(get(acc, 'openingReClass')) || isNonZero(get(acc, 'closingReClass'));
    };

    const checkFli = (fli: unknown): boolean => {
        if (fli == null) return false;
        const accounts = get(fli, 'accounts');
        if (Array.isArray(accounts)) {
            for (const a of accounts) {
                if (a == null) continue;
                const t = get(a, 'type');
                if (t === 'SUB FLI' || t === 'OF_THAT_ITEM') {
                    const subAccounts = get(a, 'accounts');
                    if (Array.isArray(subAccounts)) {
                        for (const sa of subAccounts) if (checkAccount(sa)) return true;
                    }
                    const subReclasses = get(a, 'reclassesAccounts');
                    if (Array.isArray(subReclasses)) {
                        for (const ra of subReclasses) if (checkAccount(ra)) return true;
                    }
                } else if (checkAccount(a)) {
                    return true;
                }
            }
        }
        const fliReclasses = get(fli, 'reclassesAccounts');
        if (Array.isArray(fliReclasses)) {
            for (const ra of fliReclasses) if (checkAccount(ra)) return true;
        }
        return false;
    };

    const walkGroup = (g: unknown): boolean => {
        if (g == null) return false;
        const items = get(g, 'items');
        if (Array.isArray(items)) {
            for (const item of items) {
                if (item == null) continue;
                if (get(item, 'type') === 'FLI' && checkFli(item)) return true;
            }
        }
        const groups = get(g, 'groups');
        if (Array.isArray(groups)) {
            for (const sub of groups) {
                if (walkGroup(sub)) return true;
            }
        }
        return false;
    };

    for (const fs of finStatements) {
        const template = fs?.templateAndAccountsMapping?.template;
        if (template == null) continue;
        if (walkGroup(get(template, 'group'))) return true;
    }
    return false;
}

/**
 * Content-based classifier for whether an AR belongs to the "new world" of
 * transactional reclassifications (grouping & masking) rather than the
 * legacy reclasses tab. Replaces the former date-based cutover gate.
 *
 * Returns `true` when the AR has explicitly saved the new reclass model
 * (`groupingAndMasking.reclassificationsVersion === 1`) OR carries no
 * non-zero legacy reclass values on its finStatements. Returns `false`
 * for missing ARs and for reports that still hold legacy reclass values.
 */
export function isReclassNewWorld(ar: IAnnualReport | null | undefined): boolean {
    if (ar == null) return false;
    if (ar?.groupingAndMasking?.reclassificationsVersion === 1) return true;
    return !hasLegacyReclasses(ar?.finStatements);
}

/**
 * sessionStorage key for "the user has dismissed the legacy-reclasses
 * autofill warning for this AR during the current tab session". Per-AR
 * so opening a different report still surfaces the warning.
 */
export function legacyReclassesAutofillWarningSeenKey(annualReportId: string | null | undefined): string {
    return `aumico:legacyReclassesAutofillWarningSeen:${annualReportId ?? 'unknown'}`;
}
